import {
    useFloating,
    autoUpdate,
    useInteractions,
    useHover,
    useFocus,
    type UseFloatingOptions,
    type OpenChangeReason,
    type UseHoverProps,
    useClick,
    type UseClickProps,
    useRole,
    useDismiss,
    type UseDismissProps,
    type UseFocusProps,
    type UseRoleProps,
    useDelayGroup,
    arrow,
    type ArrowOptions,
    offset,
    type OffsetOptions,
    shift,
    type ShiftOptions,
    flip,
    type FlipOptions,
    autoPlacement,
    type AutoPlacementOptions,
    hide,
    type HideOptions,
    useTransitionStyles,
    type UseTransitionStylesProps,
    type Middleware,
    useListNavigation,
    type UseListNavigationProps,
} from "@floating-ui/react";
import { useLayoutEffect, useState, type CSSProperties, type MutableRefObject } from "react";

// Re-export FloatingFocusManager and FloatingPortal for consumers
export { FloatingFocusManager, FloatingPortal, FloatingArrow } from "@floating-ui/react";

export interface ListNavigationOptions {
    enabled?: boolean;
    listRef: MutableRefObject<(HTMLElement | null)[]>;
    activeIndex: number | null;
    onNavigate?: (index: number | null) => void;
    loop?: boolean;
    virtual?: boolean;
    orientation?: "vertical" | "horizontal" | "both";
    focusItemOnOpen?: boolean | "auto";
    focusItemOnHover?: boolean;
    openOnArrowKeyDown?: boolean;
    disabledIndices?: number[];
    allowEscape?: boolean;
    cols?: number;
    scrollItemIntoView?: boolean | ScrollIntoViewOptions;
}

interface UseFloatingPositionPropsBase {
    placement?: UseFloatingOptions["placement"];
    strategy?: UseFloatingOptions["strategy"];
    transform?: UseFloatingOptions["transform"];
    open?: boolean;
    onOpenChange?(open: boolean, event?: Event, reason?: OpenChangeReason): void;
    dismissOptions?: UseDismissProps;
    focusOptions?: UseFocusProps;
    roleOptions?: UseRoleProps;
    showArrow?: boolean;
    arrowRef?: ArrowOptions["element"];
    arrowPadding?: number;
    enableDelayGroups?: boolean;
    transitionOptions?: UseTransitionStylesProps;
    customMiddleware?: Middleware[];
    disableAutoUpdate?: boolean;
    middlewareOptions?: {
        offset?: { enabled?: boolean; config?: OffsetOptions };
        shift?: { enabled?: boolean; config?: ShiftOptions };
        flip?: { enabled?: boolean; config?: FlipOptions };
        autoPlacement?: { enabled?: boolean; config?: AutoPlacementOptions };
        hide?: { enabled?: boolean; config?: HideOptions };
    };
    // List navigation for keyboard support
    listNavigationOptions?: ListNavigationOptions;
}

interface UseFloatingPositionPropsHover extends UseFloatingPositionPropsBase {
    enableTriggerOnClick?: false;
    enableHover?: boolean;
    hoverOptions?: Omit<UseHoverProps, "enabled">;
}

interface UseFloatingPositionPropsClick extends UseFloatingPositionPropsBase {
    enableTriggerOnClick: true;
    clickOptions?: Omit<UseClickProps, "enabled">;
}

export type UseFloatingPositionProps =
    | UseFloatingPositionPropsHover
    | UseFloatingPositionPropsClick;

export const useFloatingPosition = ({
    placement,
    strategy,
    transform,
    open,
    onOpenChange,
    enableTriggerOnClick = false,
    dismissOptions,
    focusOptions,
    roleOptions,
    showArrow = false,
    arrowRef,
    arrowPadding,
    enableDelayGroups = true,
    transitionOptions,
    customMiddleware,
    disableAutoUpdate = false,
    middlewareOptions = {},
    listNavigationOptions,
    ...options
}: UseFloatingPositionProps) => {
    const [state, setState] = useState<"unmounted" | "initial" | "positioned">(
        "unmounted"
    );

    const middlewares: UseFloatingOptions["middleware"] = [];

    const offsetConfig = middlewareOptions.offset;
    if (offsetConfig?.enabled !== false) {
        middlewares.push(offset(offsetConfig?.config || 5));
    }

    const shiftConfig = middlewareOptions.shift;
    if (shiftConfig?.enabled === true) {
        middlewares.push(shift(shiftConfig?.config));
    }

    // flip and autoPlacement are mutually exclusive
    const flipConfig = middlewareOptions.flip;
    const autoPlacementConfig = middlewareOptions.autoPlacement;

    if (flipConfig?.enabled !== false && autoPlacementConfig?.enabled !== true) {
        middlewares.push(flip(flipConfig?.config));
    } else if (
        autoPlacementConfig?.enabled === true &&
        flipConfig?.enabled !== true
    ) {
        middlewares.push(autoPlacement(autoPlacementConfig?.config));
    }

    if (customMiddleware && customMiddleware.length > 0) {
        middlewares.push(...customMiddleware);
    }

    if (showArrow && arrowRef) {
        middlewares.push(arrow({ element: arrowRef, padding: arrowPadding }));
    }

    const hideConfig = middlewareOptions.hide;
    if (hideConfig?.enabled === true) {
        middlewares.push(hide(hideConfig?.config));
    }

    const {
        context,
        middlewareData,
        isPositioned,
        placement: computedPlacement,
        update,
        ...rest
    } = useFloating({
        placement,
        strategy,
        transform,
        open,
        onOpenChange(open) {
            onOpenChange?.(open);
            if (open) {
                if (state === "unmounted") setState("initial");
            }
        },
        whileElementsMounted: disableAutoUpdate ? undefined : autoUpdate,
        middleware: middlewares,
    });

    const delayGroupContext = useDelayGroup(context, {
        enabled: enableDelayGroups,
    });

    const enableHover = "enableHover" in options ? options.enableHover : true;
    const hoverOpts = "hoverOptions" in options ? options.hoverOptions : {};

    // Determine the delay for hover interactions:
    // - When delay groups are disabled, use the custom hoverOptions delay
    // - When in instant phase (rapidly moving between tooltips in a group), use group delay
    // - When inside an active FloatingDelayGroup (delay > 0), use group delay
    // - Otherwise use custom hoverOptions delay
    const getDelay = () => {
        if (!enableDelayGroups) {
            return hoverOpts?.delay;
        }

        const groupDelay = delayGroupContext?.delay;
        // Check if we're inside an actual FloatingDelayGroup
        // (delay is 0 by default if no provider exists)
        const hasGroupProvider =
            typeof groupDelay === "number"
                ? groupDelay > 0
                : typeof groupDelay === "object" &&
                  groupDelay !== null &&
                  ((groupDelay.open ?? 0) > 0 || (groupDelay.close ?? 0) > 0);

        // In instant phase or inside an active group, use group delay
        if (delayGroupContext?.isInstantPhase || hasGroupProvider) {
            return groupDelay;
        }

        // No active group, use custom delay
        return hoverOpts?.delay;
    };

    const hover = useHover(context, {
        ...hoverOpts,
        enabled: !enableTriggerOnClick && enableHover !== false,
        delay: getDelay(),
    });
    const click = useClick(context, {
        ...("clickOptions" in options ? options.clickOptions : {}),
        enabled: enableTriggerOnClick,
    });
    const focus = useFocus(context, focusOptions);
    const role = useRole(context, roleOptions);
    const dismiss = useDismiss(context, dismissOptions);

    // List navigation for keyboard support (arrow keys)
    const listNavigation = useListNavigation(context, {
        listRef: listNavigationOptions?.listRef ?? { current: [] },
        activeIndex: listNavigationOptions?.activeIndex ?? null,
        onNavigate: listNavigationOptions?.onNavigate,
        loop: listNavigationOptions?.loop ?? true,
        virtual: listNavigationOptions?.virtual,
        orientation: listNavigationOptions?.orientation ?? "vertical",
        focusItemOnOpen: listNavigationOptions?.focusItemOnOpen ?? "auto",
        focusItemOnHover: listNavigationOptions?.focusItemOnHover ?? true,
        openOnArrowKeyDown: listNavigationOptions?.openOnArrowKeyDown ?? true,
        disabledIndices: listNavigationOptions?.disabledIndices,
        allowEscape: listNavigationOptions?.allowEscape,
        cols: listNavigationOptions?.cols,
        scrollItemIntoView: listNavigationOptions?.scrollItemIntoView,
        enabled: listNavigationOptions?.enabled ?? false,
    });

    const interactionProps = useInteractions([
        hover,
        click,
        focus,
        role,
        dismiss,
        listNavigation,
    ]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(
        context,
        transitionOptions
    );

    useLayoutEffect(() => {
        if (isPositioned && state !== "positioned") {
            setState("positioned");
        }
    }, [isPositioned, state]);

    return {
        state,
        middlewareData,
        context,
        computedPlacement,
        isPositioned,
        isMounted,
        transitionStyles: transitionStyles as CSSProperties,
        update,
        ...rest,
        ...interactionProps,
    };
};
